import { Badge, Box, Heading, HStack, Text } from '@chakra-ui/react';
import { MentionBoostDeliveryState, MentionBoostLaunchFailureReason } from '@fluenceur/types';
import { WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

/**
 * Visual tone applied to the delivery-state badge and reason card. Maps onto
 * Chakra `colorPalette` values, mirroring the eligibility panel's tone
 * vocabulary so the launched view stays consistent with the setup view.
 */
export type DeliveryTone = 'gray' | 'blue' | 'green' | 'orange' | 'red';

/**
 * Honest delivery-state tone, the single source of truth for the badge colour
 * across the settled card and the inline status row. Exported so the unified
 * inline view reads the same vocabulary instead of forking its own map.
 */
export const DELIVERY_TONE: Record<MentionBoostDeliveryState, DeliveryTone> = {
  [MentionBoostDeliveryState.DRAFT]: 'gray',
  [MentionBoostDeliveryState.PUBLISHING]: 'blue',
  [MentionBoostDeliveryState.ACTIVE]: 'green',
  [MentionBoostDeliveryState.NEEDS_ATTENTION]: 'orange',
  [MentionBoostDeliveryState.FAILED]: 'red',
  [MentionBoostDeliveryState.STOPPING]: 'gray',
  [MentionBoostDeliveryState.STOPPED]: 'gray',
};

/**
 * Delivery states whose card IS the failure reason, not a generic status line.
 * Exported so the inline status view classifies states from the same source.
 */
export const REASON_BEARING_STATES = new Set<MentionBoostDeliveryState>([
  MentionBoostDeliveryState.FAILED,
  MentionBoostDeliveryState.NEEDS_ATTENTION,
]);

const FAILURE_REASONS = new Set<string>(Object.values(MentionBoostLaunchFailureReason));

/**
 * Maps a raw `lastErrorCode` onto a known, translatable failure-reason key.
 * Anything unrecognised collapses to `unknown` so the marketer never sees a
 * raw provider string.
 */
function resolveFailureReason(
  lastErrorCode: string | null | undefined
): MentionBoostLaunchFailureReason {
  if (lastErrorCode && FAILURE_REASONS.has(lastErrorCode)) {
    return lastErrorCode as MentionBoostLaunchFailureReason;
  }

  return MentionBoostLaunchFailureReason.UNKNOWN;
}

/**
 * Whether this card leads with a cause instead of a generic status line.
 *
 * Failed and needs-attention always do: there, the card IS the reason. A
 * STOPPED boost does so only when a cause was actually recorded — the two stops
 * the system decides for the brand, a creator withdrawing consent and a display
 * rights term running out. Those are the moments the brand has something to do
 * next, contact the creator or renew the rights, and the copy for them is
 * written for exactly this screen.
 *
 * An ordinary stop the brand asked for records no cause and keeps its one
 * honest line: it knows why it clicked. An unrecognised code does too, rather
 * than telling a brand that ended its own boost that something went "unknown".
 *
 * Exported so the inline live view leads with the same card as the settled one.
 */
export function leadsWithReason(
  deliveryState: MentionBoostDeliveryState,
  lastErrorCode: string | null | undefined
): boolean {
  if (REASON_BEARING_STATES.has(deliveryState)) {
    return true;
  }

  return (
    deliveryState === MentionBoostDeliveryState.STOPPED &&
    Boolean(lastErrorCode) &&
    FAILURE_REASONS.has(lastErrorCode as string)
  );
}

export interface BoostDeliveryStatusProps {
  deliveryState: MentionBoostDeliveryState;
  lastErrorCode?: string | null;
  /**
   * Drops the title + badge row, rendering only the status/reason body. The
   * inline live view supplies its own compact header, so the needs-attention
   * reason card must not duplicate the "Partnership Ads" heading there.
   */
  hideHeader?: boolean;
}

/**
 * Presentational, prop-driven view of a launched boost's delivery state.
 *
 * A healthy state shows an honest one-line status. A failed / needs-attention
 * state leads directly with the marketer-safe failure reason — a single
 * tone-tinted card with the reason title and a clear next step — instead of
 * wrapping it in a redundant "review the reason below" notice. It invents no
 * progress bars, metrics, or batch ranks.
 *
 * A `draft` boost is not yet launched and renders nothing; the setup view owns
 * that surface.
 */
export function BoostDeliveryStatus({
  deliveryState,
  lastErrorCode,
  hideHeader = false,
}: BoostDeliveryStatusProps) {
  const { t } = useTranslation();

  if (deliveryState === MentionBoostDeliveryState.DRAFT) {
    return null;
  }

  const tone = DELIVERY_TONE[deliveryState];

  const body = leadsWithReason(deliveryState, lastErrorCode) ? (
    <BoostDeliveryReason failureReason={resolveFailureReason(lastErrorCode)} tone={tone} />
  ) : (
    <Box
      border="1px solid"
      borderColor={`${tone}.200`}
      rounded="lg"
      bg={`${tone}.50`}
      px={3}
      py={3}
    >
      <Text fontSize="sm" color={`${tone}.800`} lineHeight="tall">
        {t(`mentions.boost.delivery.${deliveryState}.copy`)}
      </Text>
    </Box>
  );

  if (hideHeader) {
    return body;
  }

  return (
    <Box py={5}>
      <HStack justify="space-between" mb={3} gap={3}>
        <Heading size="sm" color="gray.900">
          {t('mentions.boost.title')}
        </Heading>
        <Badge colorPalette={tone} variant="subtle" textTransform="none" flexShrink={0}>
          {t(`mentions.boost.delivery.${deliveryState}.label`)}
        </Badge>
      </HStack>

      {body}
    </Box>
  );
}

interface BoostDeliveryReasonProps {
  failureReason: MentionBoostLaunchFailureReason;
  tone: DeliveryTone;
}

/**
 * Marketer-safe explanation for why a launched boost failed or needs attention,
 * mapped from the boost's `lastErrorCode`. The reason is the hero of the card:
 * a tone icon, a title, and one honest next-step line.
 */
function BoostDeliveryReason({ failureReason, tone }: BoostDeliveryReasonProps) {
  const { t } = useTranslation();

  return (
    <HStack
      align="flex-start"
      gap={3}
      border="1px solid"
      borderColor={`${tone}.200`}
      bg={`${tone}.50`}
      rounded="lg"
      px={3.5}
      py={3.5}
    >
      <Box color={`${tone}.600`} flexShrink={0} mt={0.5}>
        <WarningTriangle width={18} height={18} />
      </Box>
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="semibold" color={`${tone}.900`}>
          {t(`mentions.boost.launchFailure.${failureReason}.title`)}
        </Text>
        <Text fontSize="sm" color={`${tone}.800`} lineHeight="1.5" mt={1}>
          {t(`mentions.boost.launchFailure.${failureReason}.description`)}
        </Text>
      </Box>
    </HStack>
  );
}
